import React from 'react';
import { connect } from 'react-redux';
import { ApiProvider } from '@leverege/api-redux';
import { DataSourceFactory, Attributes } from '@leverege/ui-attributes';
import { DateTimeRangePicker } from '@leverege/ui-elements';
import { ItemDelegate } from '@leverege/molten-core';
import { ValuesCache } from '@leverege/value-cache';
import { getQueryTrigger } from "../../../history/Util.js";
/**
 * This will load data from API and make it available as items to the actual renderer
 */
import { jsx as _jsx } from "react/jsx-runtime";
class HistoryLoadDataRenderer extends React.Component {
  static contextType = ApiProvider.Context;
  constructor(props) {
    super(props);
    this.lastConvert = {
      items: []
    };
    this.isInError = false;
    this.cache = new ValuesCache();
    const {
      relationship,
      delegate,
      dataViewerCardModel,
      dataViewer,
      attributeName
    } = props;
    this.trigger = getQueryTrigger({
      objectType: relationship.objectType,
      path: relationship.path,
      client: 'HistoryView',
      dataViewerType: dataViewer?.type || dataViewerCardModel?.dataViewer?.type,
      queryName: `${attributeName}.heatmap`,
      relationship,
      delegate,
      caches: this.cache,
      loadData: this.loadData,
      shouldReloadData: this.shouldReloadData
    }, this.getContext());
  }
  componentDidMount() {
    this.trigger?.init?.();
  }
  componentDidUpdate(prevProps, prevState, snapshot) {
    const {
      model,
      dashboardContext,
      Model
    } = this.props;
    const {
      model: pModel,
      dashboardContext: pDashboardContext
    } = prevProps;
    if (queryOptionsChanged(model, pModel, dashboardContext, pDashboardContext, Model)) {
      this.loadData();
    }
  }
  componentWillUnmount() {
    this.unmounted = true;
    this.trigger?.cleanup?.();
  }
  getContext() {
    return {
      client: 'HistoryLoadDataRenderer',
      clientProps: {
        ...this.props
      }
    };
  }

  // TODO Consolidate into one function
  createDataItems = results => {
    const {
      relationship,
      delegate
    } = this.props;
    const page = 0;
    const {
      items
    } = results;
    const type = `${relationship.blueprint.type}.history`;
    const dataSource = DataSourceFactory.get(type) || {
      getData: () => null
    };
    const deviceHistories = {};
    items?.forEach(item => {
      const ref = `${delegate.getInterfaceUrl()}/${item.deviceId}.history`;
      const obj = {
        ...item,
        type,
        ref
      };
      const data = dataSource.getData(obj);
      obj.data = data;
      if (!deviceHistories[obj.deviceId]) {
        deviceHistories[obj.deviceId] = {
          id: `${obj.deviceId}.history`,
          items: []
        };
      }
      deviceHistories[obj.deviceId].items.push(obj);
    });

    // TODO: Remove first item from array because it will be the same as the current position?
    // historyItems.shift()

    return {
      status: results?.status || null,
      loading: results?.status === 'api.interface.status.loading',
      perPage: results.perPage || 1000,
      page,
      count: results?.total || 0,
      items: Object.values(deviceHistories)
    };
  };
  getItems() {
    const {
      history
    } = this.props;

    // simple cache
    if (history === this.lastHistory) {
      return this.lastConvert;
    }
    this.lastConvert = this.createDataItems(history);
    this.lastHistory = history;
    return this.lastConvert;
  }
  loadData = async () => {
    if (this.isInError) return;
    const {
      dispatch,
      delegate,
      relationship,
      model,
      items,
      attributeName,
      dashboardContext,
      dataViewerCardModel,
      Model
    } = this.props;
    const {
      blueprint
    } = relationship;
    if (this.unmounted) {
      return;
    }
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }
    const geoPosAttr = Attributes.getAttribute(attributeName, blueprint.historyType);
    // remove the '/data' before the path
    const attrPath = geoPosAttr?.blueprint?.field?.substring(5);
    if (delegate.history && geoPosAttr && attrPath) {
      let range;
      let condition;
      if (dataViewerCardModel && dataViewerCardModel.needsDate) {
        range = dashboardContext.options.dateTimeRange;
      } else {
        range = Model.getRange(model);
      }
      if (dataViewerCardModel && dataViewerCardModel.needsCondition) {
        condition = dashboardContext.options.condition;
      } else {
        condition = Model.getCondition(model);
      }
      const filter = condition ? delegate.historyDelegate.convertToFilter(condition, {
        objectType: `${relationship.objectType}.history`
      }) : null;
      range = DateTimeRangePicker.getRange(range);
      const perPage = Model.getPerPage(model);
      try {
        let p = 0;
        let hasMoreData = true;
        const where = items?.length === 1 ? [[[attrPath, null, null, 'object'], '<>', null]] : [['$deviceId', 'IN', items?.map(item => item.id) || []], [[attrPath, null, null, 'object'], '<>', null]];
        if (filter) {
          where.push(...filter);
        }
        const q = {
          ...range,
          perPage,
          startIndex: p * perPage,
          where,
          order: [['$time', 'DESC']]
        };
        this.trigger?.preQuery?.(q);
        while (hasMoreData) {
          q.startIndex = p * perPage;
          // eslint-disable-next-line no-await-in-loop
          const {
            items: hItems
          } = await dispatch(delegate.historyDelegate.search({
            query: q,
            queryName: `${attributeName}.heatmap`
          }));
          p += 1;
          if (hItems.length < perPage) {
            hasMoreData = false;
          }
        }
        if (this.unmounted !== true) {
          this.trigger?.postQuery?.();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        this.isInError = true;
        if (this.unmounted !== true) {
          this.trigger?.onQueryError?.(err);
        }
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('Loader action does not have a history', delegate);
    }
  };
  render() {
    // const { items } = this.state
    const {
      renderer: Renderer
    } = this.props;
    const items = this.getItems().items;
    return /*#__PURE__*/_jsx(Renderer, {
      ...this.props,
      items: items
    });
  }
}
const EMPTY_OBJ = {};
function Bailout(props) {
  const {
    model
  } = props;
  if (!model?.visible) {
    return null;
  }
  return /*#__PURE__*/_jsx(HistoryLoadDataRenderer, {
    ...props
  });
}
export default connect((state, props) => {
  const {
    delegate: pDelegate,
    attributeName,
    items
  } = props;
  const delegate = pDelegate instanceof ItemDelegate ? pDelegate : items?.length === 1 ? pDelegate.item(items[0].id) : pDelegate;
  const queryData = delegate.historyDelegate.getList(state, `${attributeName}.heatmap`, {
    getAll: true
  });
  return {
    history: queryData || EMPTY_OBJ,
    delegate
  };
})(Bailout);
function queryOptionsChanged(model, pModel, dashboardContext, pDashboardContext, Model) {
  const range = Model.getRange(model);
  const pRange = Model.getRange(pModel);
  const perPage = Model.getPerPage(model);
  const pPerPage = Model.getPerPage(pModel);
  const condition = Model.getCondition(model);
  const pCondition = Model.getCondition(pModel);
  if (range !== pRange || perPage !== pPerPage || condition !== pCondition || dashboardContext?.options?.dateTimeRange !== pDashboardContext?.options?.dateTimeRange || dashboardContext?.options?.condition !== pDashboardContext?.options?.condition) {
    return true;
  }
  return false;
}